import PageHeader from '../../components/PageHeader';
// src/pages/ActivityPage.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  Avatar,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  LinearProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
} from '@mui/material';
import {
  DirectionsCar as CarIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Engineering as EngineeringIcon,
  Route as RouteIcon,
  Speed as SpeedIcon,
  Schedule as ScheduleIcon,
  LocalGasStation as FuelIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Visibility as ViewIcon,
  GetApp as DownloadIcon,
  DateRange as DateRangeIcon,
  AccessTime as AccessTimeIcon,
  Place as PlaceIcon,
  Navigation as NavigationIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

interface Trip {
  id: string;
  vehicleId: string;
  plateNumber: string;
  category: 'executive' | 'staff_bus' | 'technical' | 'general';
  driverName: string;
  startTime: Date;
  endTime: Date;
  startLocation: string;
  endLocation: string;
  distance: number; // in km
  duration: number; // in minutes
  maxSpeed: number;
  averageSpeed: number;
  fuelConsumed: number; // in liters
  idleTime: number; // in minutes
  violations: string[];
  purpose?: string;
  department?: string;
}

interface ActivitySummary {
  totalTrips: number;
  totalDistance: number;
  totalDuration: number; // in minutes
  totalFuelConsumed: number;
  averageSpeed: number;
  violationsCount: number;
  uniqueVehicles: number;
  uniqueDrivers: number;
}

interface DailyActivity {
  date: string;
  trips: number;
  distance: number;
  duration: number;
  fuelConsumed: number;
}

const ActivityPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [summary, setSummary] = useState<ActivitySummary | null>(null);
  const [dailyActivity, setDailyActivity] = useState<DailyActivity[]>([]);
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [openTripDetails, setOpenTripDetails] = useState(false);
  const [dateRange, setDateRange] = useState('7'); // days
  const [filterVehicle, setFilterVehicle] = useState('');
  const [filterDriver, setFilterDriver] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  // Mock data
  useEffect(() => {
    const mockTrips: Trip[] = [
      {
        id: '1',
        vehicleId: 'v1',
        plateNumber: 'ABC-123',
        category: 'executive',
        driverName: 'John Doe',
        startTime: new Date('2024-09-21T08:30:00'),
        endTime: new Date('2024-09-21T09:15:00'),
        startLocation: 'Corporate Office, Victoria Island',
        endLocation: 'Airport, Ikeja',
        distance: 25.3,
        duration: 45,
        maxSpeed: 80,
        averageSpeed: 33.7,
        fuelConsumed: 3.2,
        idleTime: 8,
        violations: ['Speed limit exceeded'],
        purpose: 'Airport pickup',
        department: 'Executive'
      },
      {
        id: '2',
        vehicleId: 'v2',
        plateNumber: 'XYZ-456',
        category: 'staff_bus',
        driverName: 'Jane Smith',
        startTime: new Date('2024-09-21T07:00:00'),
        endTime: new Date('2024-09-21T08:30:00'),
        startLocation: 'Staff Quarters, Mainland',
        endLocation: 'Corporate Office, Victoria Island',
        distance: 32.1,
        duration: 90,
        maxSpeed: 65,
        averageSpeed: 21.4,
        fuelConsumed: 4.8,
        idleTime: 25,
        violations: ['Excessive idling'],
        purpose: 'Staff transportation',
        department: 'HR'
      },
      {
        id: '3',
        vehicleId: 'v3',
        plateNumber: 'DEF-789',
        category: 'technical',
        driverName: 'Mike Johnson',
        startTime: new Date('2024-09-21T09:00:00'),
        endTime: new Date('2024-09-21T17:30:00'),
        startLocation: 'Corporate Office, Victoria Island',
        endLocation: 'Site Location, Lekki',
        distance: 156.7,
        duration: 510,
        maxSpeed: 85,
        averageSpeed: 18.4,
        fuelConsumed: 18.5,
        idleTime: 120,
        violations: ['Speed limit exceeded', 'Harsh braking'],
        purpose: 'Site inspection',
        department: 'Engineering'
      },
      {
        id: '4',
        vehicleId: 'v1',
        plateNumber: 'ABC-123',
        category: 'executive',
        driverName: 'John Doe',
        startTime: new Date('2024-09-20T14:00:00'),
        endTime: new Date('2024-09-20T16:30:00'),
        startLocation: 'Corporate Office, Victoria Island',
        endLocation: 'Client Meeting, Ikoyi',
        distance: 18.5,
        duration: 150,
        maxSpeed: 70,
        averageSpeed: 7.4,
        fuelConsumed: 2.1,
        idleTime: 90,
        violations: ['Excessive idling'],
        purpose: 'Client meeting',
        department: 'Executive'
      },
    ];

    setTrips(mockTrips);

    // Calculate summary
    const summaryData: ActivitySummary = {
      totalTrips: mockTrips.length,
      totalDistance: mockTrips.reduce((sum, trip) => sum + trip.distance, 0),
      totalDuration: mockTrips.reduce((sum, trip) => sum + trip.duration, 0),
      totalFuelConsumed: mockTrips.reduce((sum, trip) => sum + trip.fuelConsumed, 0),
      averageSpeed: mockTrips.reduce((sum, trip) => sum + trip.averageSpeed, 0) / mockTrips.length,
      violationsCount: mockTrips.reduce((sum, trip) => sum + trip.violations.length, 0),
      uniqueVehicles: new Set(mockTrips.map(trip => trip.vehicleId)).size,
      uniqueDrivers: new Set(mockTrips.map(trip => trip.driverName)).size,
    };
    setSummary(summaryData);

    // Generate daily activity data for the last 7 days
    const dailyData: DailyActivity[] = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dayTrips = mockTrips.filter(trip => {
        const tripDate = new Date(trip.startTime);
        return tripDate.toDateString() === date.toDateString();
      });
      
      dailyData.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        trips: dayTrips.length,
        distance: dayTrips.reduce((sum, trip) => sum + trip.distance, 0),
        duration: dayTrips.reduce((sum, trip) => sum + trip.duration, 0),
        fuelConsumed: dayTrips.reduce((sum, trip) => sum + trip.fuelConsumed, 0),
      });
    }
    setDailyActivity(dailyData);
  }, []);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'executive': return <PersonIcon />;
      case 'staff_bus': return <BusinessIcon />;
      case 'technical': return <EngineeringIcon />;
      case 'general': return <CarIcon />;
      default: return <CarIcon />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'executive': return 'primary';
      case 'staff_bus': return 'secondary';
      case 'technical': return 'warning';
      case 'general': return 'info';
      default: return 'default';
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const filteredTrips = trips.filter(trip => {
    if (filterVehicle && trip.plateNumber !== filterVehicle) return false;
    if (filterDriver && trip.driverName !== filterDriver) return false;
    if (filterCategory && trip.category !== filterCategory) return false;
    return true;
  });

  // Fixed: Using Array.from instead of spread operator for better compatibility
  const uniqueVehicles = Array.from(new Set(trips.map(trip => trip.plateNumber)));
  const uniqueDrivers = Array.from(new Set(trips.map(trip => trip.driverName)));

  const handleViewTripDetails = (trip: Trip) => {
    setSelectedTrip(trip);
    setOpenTripDetails(true);
  };

  return (
    <Box>
      {/* Header */}
      <PageHeader title="Fleet Activity" description="Review journeys, driving time, and vehicle activity" icon="trips" actions={<>
<Stack direction="row" spacing={2}>
          <Button variant="outlined" startIcon={<DownloadIcon />}>
            Export Data
          </Button>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={dateRange}
              label="Period"
              onChange={(e) => setDateRange(e.target.value)}
            >
              <MenuItem value="1">Today</MenuItem>
              <MenuItem value="7">Last 7 days</MenuItem>
              <MenuItem value="30">Last 30 days</MenuItem>
              <MenuItem value="90">Last 90 days</MenuItem>
            </Select>
          </FormControl>
        </Stack>
</>} />

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <RouteIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {summary.totalTrips}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Trips
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'success.main' }}>
                    <NavigationIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {summary.totalDistance.toFixed(1)}km
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Distance
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'info.main' }}>
                    <FuelIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {summary.totalFuelConsumed.toFixed(1)}L
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Fuel Consumed
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: summary.violationsCount > 0 ? 'warning.main' : 'success.main' }}>
                    <WarningIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {summary.violationsCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Violations
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Card>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Recent Trips" />
          <Tab label="Daily Activity" />
          <Tab label="Driver Performance" />
          <Tab label="Vehicle Utilization" />
        </Tabs>

        <CardContent>
          {activeTab === 0 && (
            <Box>
              {/* Filters */}
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Filter by Vehicle</InputLabel>
                      <Select
                        value={filterVehicle}
                        onChange={(e) => setFilterVehicle(e.target.value)}
                      >
                        <MenuItem value="">All Vehicles</MenuItem>
                        {uniqueVehicles.map(vehicle => (
                          <MenuItem key={vehicle} value={vehicle}>{vehicle}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Filter by Driver</InputLabel>
                      <Select
                        value={filterDriver}
                        onChange={(e) => setFilterDriver(e.target.value)}
                      >
                        <MenuItem value="">All Drivers</MenuItem>
                        {uniqueDrivers.map(driver => (
                          <MenuItem key={driver} value={driver}>{driver}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Filter by Category</InputLabel>
                      <Select
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                      >
                        <MenuItem value="">All Categories</MenuItem>
                        <MenuItem value="executive">Executive Pool</MenuItem>
                        <MenuItem value="staff_bus">Staff Bus</MenuItem>
                        <MenuItem value="technical">Technical Units</MenuItem>
                        <MenuItem value="general">General Pool</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>

              {/* Trips Table */}
              <Box sx={{ width: '100%', overflowX: 'auto' }}><Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Vehicle</TableCell>
                    <TableCell>Driver</TableCell>
                    <TableCell>Route</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Distance</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Avg Speed</TableCell>
                    <TableCell>Violations</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTrips.map((trip) => (
                    <TableRow key={trip.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: getCategoryColor(trip.category) + '.main', width: 32, height: 32 }}>
                            {getCategoryIcon(trip.category)}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {trip.plateNumber}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{trip.driverName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {trip.startLocation}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            to {trip.endLocation}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {trip.startTime.toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {trip.startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{trip.distance.toFixed(1)} km</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDuration(trip.duration)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{trip.averageSpeed.toFixed(1)} km/h</Typography>
                      </TableCell>
                      <TableCell>
                        {trip.violations.length > 0 ? (
                          <Chip
                            size="small"
                            color="warning"
                            label={`${trip.violations.length} violations`}
                          />
                        ) : (
                          <Chip size="small" color="success" label="Clean" />
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleViewTripDetails(trip)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></Box>
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>Daily Activity Trends</Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, height: 300 }}>
                    <Typography variant="subtitle1" gutterBottom>Daily Distance</Typography>
                    <ResponsiveContainer width="100%" height="90%">
                      <LineChart data={dailyActivity}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip />
                        <Line type="monotone" dataKey="distance" stroke="#1976d2" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, height: 300 }}>
                    <Typography variant="subtitle1" gutterBottom>Daily Trips</Typography>
                    <ResponsiveContainer width="100%" height="90%">
                      <BarChart data={dailyActivity}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip />
                        <Bar dataKey="trips" fill="#2e7d32" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {activeTab === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>Driver Performance</Typography>
              <Grid container spacing={3}>
                {uniqueDrivers.map(driverName => {
                  const driverTrips = trips.filter(trip => trip.driverName === driverName);
                  const totalDistance = driverTrips.reduce((sum, trip) => sum + trip.distance, 0);
                  const totalViolations = driverTrips.reduce((sum, trip) => sum + trip.violations.length, 0);
                  const avgSpeed = driverTrips.reduce((sum, trip) => sum + trip.averageSpeed, 0) / driverTrips.length;

                  return (
                    <Grid item xs={12} md={6} key={driverName}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar>
                              <PersonIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="h6">{driverName}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {driverTrips.length} trips
                              </Typography>
                            </Box>
                          </Box>
                          <Stack spacing={1}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">Distance:</Typography>
                              <Typography variant="body2">{totalDistance.toFixed(1)} km</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">Avg Speed:</Typography>
                              <Typography variant="body2">{avgSpeed.toFixed(1)} km/h</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">Violations:</Typography>
                              <Chip
                                size="small"
                                color={totalViolations > 0 ? 'warning' : 'success'}
                                label={totalViolations}
                              />
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}

          {activeTab === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom>Vehicle Utilization</Typography>
              <Grid container spacing={3}>
                {uniqueVehicles.map(plateNumber => {
                  const vehicleTrips = trips.filter(trip => trip.plateNumber === plateNumber);
                  const vehicle = vehicleTrips[0];
                  const totalDistance = vehicleTrips.reduce((sum, trip) => sum + trip.distance, 0);
                  const totalDuration = vehicleTrips.reduce((sum, trip) => sum + trip.duration, 0);
                  const totalFuel = vehicleTrips.reduce((sum, trip) => sum + trip.fuelConsumed, 0);

                  return (
                    <Grid item xs={12} md={6} key={plateNumber}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{ bgcolor: getCategoryColor(vehicle.category) + '.main' }}>
                              {getCategoryIcon(vehicle.category)}
                            </Avatar>
                            <Box>
                              <Typography variant="h6">{plateNumber}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {vehicle.category.replace('_', ' ')} • {vehicleTrips.length} trips
                              </Typography>
                            </Box>
                          </Box>
                          <Stack spacing={1}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">Distance:</Typography>
                              <Typography variant="body2">{totalDistance.toFixed(1)} km</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">Usage Time:</Typography>
                              <Typography variant="body2">{formatDuration(totalDuration)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">Fuel Used:</Typography>
                              <Typography variant="body2">{totalFuel.toFixed(1)}L</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">Efficiency:</Typography>
                              <Typography variant="body2">{(totalDistance / totalFuel).toFixed(1)} km/L</Typography>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Trip Details Dialog */}
      <Dialog open={openTripDetails} onClose={() => setOpenTripDetails(false)} maxWidth="md" fullWidth>
        <DialogTitle>Trip Details</DialogTitle>
        <DialogContent>
          {selectedTrip && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Trip Information</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><CarIcon /></ListItemIcon>
                      <ListItemText
                        primary="Vehicle"
                        secondary={`${selectedTrip.plateNumber} (${selectedTrip.category.replace('_', ' ')})`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><PersonIcon /></ListItemIcon>
                      <ListItemText primary="Driver" secondary={selectedTrip.driverName} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><DateRangeIcon /></ListItemIcon>
                      <ListItemText
                        primary="Date & Time"
                        secondary={`${selectedTrip.startTime.toLocaleDateString()} ${selectedTrip.startTime.toLocaleTimeString()}`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><AccessTimeIcon /></ListItemIcon>
                      <ListItemText primary="Duration" secondary={formatDuration(selectedTrip.duration)} />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Route & Performance</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><PlaceIcon /></ListItemIcon>
                      <ListItemText primary="From" secondary={selectedTrip.startLocation} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><NavigationIcon /></ListItemIcon>
                      <ListItemText primary="To" secondary={selectedTrip.endLocation} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><RouteIcon /></ListItemIcon>
                      <ListItemText primary="Distance" secondary={`${selectedTrip.distance.toFixed(1)} km`} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><SpeedIcon /></ListItemIcon>
                      <ListItemText
                        primary="Speed"
                        secondary={`Avg: ${selectedTrip.averageSpeed.toFixed(1)} km/h | Max: ${selectedTrip.maxSpeed} km/h`}
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
              
              {selectedTrip.violations.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Violations Detected:</Typography>
                  <ul>
                    {selectedTrip.violations.map((violation, index) => (
                      <li key={index}>{violation}</li>
                    ))}
                  </ul>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTripDetails(false)}>Close</Button>
          <Button variant="contained">View on Map</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActivityPage;